/**
 * Sprint Packet design tokens.
 *
 * Single source of truth for both surface palettes — `light` (edit mode, AE-facing,
 * warm-cream) and `dark` (present mode + PDF, client-facing, near-black) — sharing
 * one brand-purple accent, one hairline scale, one status set, one type scale, and
 * one radius/shadow scale. Consumed on-screen via inline styles and by the future
 * PDF renderer via `Font.register` + `StyleSheet.create` when that backlog item lands.
 */

export const accent = '#a044ff'
export const accentSoft = 'rgba(160, 68, 255, 0.08)'

export const status = {
  warn: '#ffb822',
  danger: '#fd397a',
  success: '#22c55e',
} as const

export const radius = { sm: 6, md: 8, lg: 12 } as const

export const shadow = {
  overlay: '0 10px 32px rgba(0,0,0,0.18)',
} as const

export const type = {
  family: {
    serif: '"Instrument Serif", Georgia, serif',
    sans: '"Inter Tight", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, monospace',
  },
  size: {
    eyebrow: 11,
    body: 14,
    bodyLg: 16,
    h3: 20,
    h2: 28,
    h1: 40,
    display: 64,
    hero: 84,
  },
  weight: { regular: 400, medium: 500, semibold: 600 },
} as const

export interface SurfacePalette {
  name: 'light' | 'dark' | 'builder'
  bg: string
  surface: string
  text: string
  textMuted: string
  textFaint: string
  hairline: string
  accent: string
  accentInk: string
  /** For warning and error text, which needs a darker tone on white than on black. */
  status: { warn: string; danger: string; success: string }
}

export const light: SurfacePalette = {
  name: 'light',
  bg: '#faf7f2',
  surface: '#ffffff',
  text: '#171717',
  textMuted: '#525252',
  textFaint: '#8a8a8a',
  hairline: '#e7e2d8',
  accent,
  accentInk: '#ffffff',
  status,
}

export const dark: SurfacePalette = {
  name: 'dark',
  bg: '#0e0e10',
  surface: '#17171a',
  text: '#f4f1ea',
  textMuted: '#a8a39a',
  textFaint: '#6c6862',
  hairline: '#2a2a2e',
  accent,
  accentInk: '#0e0e10',
  status,
}

/**
 * The ad editor where it opens over builder's own pages (the Creatives gallery,
 * the Sprints tab): white and the cool `mm-neutral-2` greys those pages use, not
 * the packet's warm cream, which reads as a different product on the gallery.
 * The status tones are the deeper steps of the same hues — the shared ones were
 * tuned for black, and on white the amber warning is about 1.7:1.
 */
export const builder: SurfacePalette = {
  name: 'builder',
  bg: '#f2f4f8',
  surface: '#ffffff',
  text: '#232330',
  textMuted: '#515478',
  textFaint: '#666a91',
  hairline: '#d5d6e2',
  accent,
  accentInk: '#ffffff',
  status: {
    warn: '#b45309',
    danger: '#c70956',
    success: '#1a7b47',
  },
}

export const palettes = { light, dark } as const
